import { InternalResource_Estimate } from '@interfaces/InternalResource_Estimate';
import { Structure } from '@interfaces/Structure';
import { ClassificationEstimate } from '../../models/interfaces/ClassificationEstimate';

export class SortHelper {
  public static sortStructures(structures: Structure[], desc = false) {
    return SortHelper.sortArrayByProperty(structures, o => o.level1?.code + o.level2?.code, desc);
  }

  public static sortArrayByCode(array: { code: string }[]) {
    return SortHelper.sortArrayByProperty(array, o => o.code);
  }

  public static sortClassificationEstimates(classificationEstimates: ClassificationEstimate[], desc = false) {
    return SortHelper.sortArrayByProperty(classificationEstimates, o => o.classification?.code, desc);
  }

  public static sortInternalResourceEstimates(internalResourceEstimates: InternalResource_Estimate[], desc = false) {
    return SortHelper.sortArrayByProperty(internalResourceEstimates, o => o.internalResource?.code, desc);
  }

  /**
   * Generic function to sort an array by one of its properties.
   * This method will mutate the array in place and will return a ref to this array.
   *
   * Usage examples:
   *
   * Sort DESC movies by releasedDate
   * SortHelper.sortArrayByProperty(Movies, (o) => o.releasedDate.getTime(), true)
   *
   * Sort DESC movies by name
   * SortHelper.sortArrayByProperty(Movies, (o) => o.name, true)
   *
   * @param array the array to sort.
   * @param getSortProperty The function that allows the sort method to select the property to sort on.
   * @param desc Defines if we will sort ASC (false) or DESC (true)
   */
  public static sortArrayByProperty<T, V>(array: T[], getSortProperty: (o: T) => V, desc = false): T[] {
    const descFactor = desc ? -1 : 1;
    return array.sort((ObjectA, ObjectB) => {
      let propertyA: V | string = getSortProperty(ObjectA);
      let propertyB: V | string = getSortProperty(ObjectB);

      if (propertyA instanceof String && propertyB instanceof String) {
        propertyA = propertyA.toLowerCase();
        propertyB = propertyB.toLowerCase();
      }

      return propertyA > propertyB ? 1 * descFactor : propertyA < propertyB ? -1 * descFactor : 0;
    });
  }
}
